import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import { useMemoizedFn } from 'ahooks';
import i18n from 'i18next';
import { useAIChatContext } from '@dauyan/ai-chat';
import { logAction } from '@/utils/logger/arms-config';
import { measureTextLines } from '@/utils/measureTextLines';
import { PointsRemain } from '@/components/pay_ui/PointsVisible';
import { SimpleTextArea } from '@/components/SimpleTextArea';
import { SendIcon } from '@/pages/Mock/Svg/SendIcon';
import { LoadingIcon } from '@/pages/Mock/Svg/LoadingIcon';
import { useDeviceType } from '@/hooks/useDeviceType';
import { checkHasPoints } from '../../Points/userPoints';
import style from './ai-chat-input.module.less';


export const isNative = () => {
  // const p = jsCookie.get('platform');
  // return p === 'android' || p === 'ios';
  return !!(window as any).X5WebView || !!window.flutter_inappwebview;
};


export interface IAIChatInputProps {
  submitQuestion: (question: string) => void;
  disabled: boolean;
  simple?: boolean;
  wordCount?: boolean;
}

export interface IAIChatInputHandles {
  focus: () => void;
  setValue: (s: string) => void;
}

export const AIChatInputHOC = (requiredPoints: number) => {

  return React.forwardRef<IAIChatInputHandles, IAIChatInputProps>(({
    submitQuestion, disabled, simple, wordCount,
  }, ref) => {

    const { is_mobile } = useDeviceType();

    const [value, setValue] = useState<string>('');

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const printingItem = !!useAIChatContext().printingItem;
    const onForceStopAction = useAIChatContext().onForceStopAction;

    const handleSubmit = useMemoizedFn(() => {
      if (!disabled && value.length) {
        if (!checkHasPoints(requiredPoints)) {
          return;
        }
        logAction('Submit_Ai_Question_With_Btn');
        submitQuestion(value);
        setValue('');
      }

    });

    const handleKeyDown = useMemoizedFn((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const ele = e.target as HTMLTextAreaElement;
      if (e.key === 'Enter' && e.keyCode === 13) {
        // shift 默认换行
        if (e.shiftKey) {
          return;
        }
        // meta alt ctrl 拦截换行
        if (e.metaKey || e.altKey || e.ctrlKey) {
          // 获取光标位置
          const start = ele.selectionStart;
          const end = ele.selectionEnd;

          // 添加换行符
          setValue(ele.value.slice(0, start) + '\n' + ele.value.slice(start));

          // 更新光标位置
          requestAnimationFrame(() => {
            ele.setSelectionRange(start + 1, end + 1);
            const height = measureTextLines(ele.value.slice(0, start), ele.computedStyleMap().get('font')?.toString() || '16px', ele.clientWidth) * 24;
            if (height < ele.scrollTop) {
              ele.scrollTop = height;
            } else if (height > ele.scrollTop + ele.clientHeight - 24) {
              ele.scrollTop = height - 24;
            }
          });
          return;
        }
        // 正常enter提交
        if (!disabled && value.length) {
          logAction('Submit_Ai_Question_With_Keyboard');
          if (!checkHasPoints(requiredPoints)) {
            return;
          }
          ele.blur();
          submitQuestion(value);
          setValue('');
          if (!isNative()) {
            requestAnimationFrame(() => {
              ele.focus();
            });
          }
        }
      }
    });

    const handleClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        },
        setValue: (s: string) => {
          setValue(s);
        },
      };
    }, []);

    if (is_mobile) {
      return <div>
        <div className="mb-4">
          <PointsRemain size="16" />
        </div>
        <div onClick={handleClick} className={classNames(style.simpleChatInputWrapper)} style={{ padding: '12px 50px 12px 12px' }}>
          <div className={classNames(style.simpleChatInput, 'ai-chat-simple-input')}>
            <span>{value || ' '}</span>
            <SimpleTextArea ref={inputRef} onKeyDown={handleKeyDown} maxLength={200} value={value} onChange={(e) => setValue(e.target.value)} placeholder={i18n.t('ai.input_placeholder')} />
          </div>
          {
            printingItem ? <LoadingIcon
              style={{ height: '32px', width: '32px', bottom: '8px' }}
              className={classNames(style.simpleChatInputLoading)}
              onClick={onForceStopAction}
            /> : <SendIcon
              style={{ height: '32px', width: '32px', bottom: '8px' }}
              onClick={handleSubmit}
              className={classNames(style.simpleChatInputSubmit, (disabled || value.length === 0) ? style.simpleChatInputSubmitDisabled : '')}
            />
          }
        </div>
        <div className={style.aiNoticeMobile}>{i18n.t('common.generate_by_ai')}</div>
      </div>;
    }


    return <div className={style.wrapper}>
      <div onClick={handleClick} className={classNames(style.simpleChatInputWrapper)}>
        <div className={classNames(style.simpleChatInput, 'ai-chat-simple-input')}>
          <span>{value || ' '}</span>
          <SimpleTextArea ref={inputRef} onKeyDown={handleKeyDown} maxLength={200} value={value} onChange={(e) => setValue(e.target.value)} placeholder={i18n.t('ai.input_placeholder')} />
        </div>
        <div className="mt-8">
          <div>
            <PointsRemain size="16" />
          </div>
          {
            printingItem ? <LoadingIcon
              style={{ height: '32px', width: '32px', bottom: '8px' }}
              className={classNames(style.simpleChatInputLoading)}
              onClick={onForceStopAction}
            /> : <SendIcon
              style={{ height: '32px', width: '32px', bottom: '8px' }}
              onClick={handleSubmit}
              className={classNames(style.simpleChatInputSubmit, (disabled || value.length === 0) ? style.simpleChatInputSubmitDisabled : '')}
            />
          }
        </div>
      </div>
      <div className={style.aiNotice}>{i18n.t('common.generate_by_ai')}</div>
    </div>;
  });
};
